import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Shared selectable option card for the entity wizard.
 *
 * Replaces the chip/card button pattern that was hand-written ~4× inside
 * Step1Extract (role grid, goals list, build-scope list, banner pills).
 *
 * - layout="tile" → icon-on-top square (used by the Role grid)
 * - layout="row"  → checkbox + label left, optional description / trailing right
 *   (used by Goals, Build Scope, Revenue stage, First task)
 */
export function SelectableCard({
  selected,
  onClick,
  layout = "row",
  icon,
  label,
  description,
  trailing,
  disabled,
  className,
  tooltip,
}: {
  selected: boolean;
  onClick: () => void;
  layout?: "tile" | "row";
  icon?: ReactNode;
  label: string;
  description?: string;
  /** Right-aligned content for row layout (e.g. price). */
  trailing?: ReactNode;
  disabled?: boolean;
  className?: string;
  /** Optional tooltip shown on hover to explain what this option means. */
  tooltip?: ReactNode;
}) {
  const tileButton = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex flex-col items-center gap-1.5 rounded-xl border p-2.5 text-center transition-all relative",
        selected
          ? "border-blue-500/60 bg-blue-500/10 text-blue-400"
          : "border-border/40 text-muted-foreground/70 hover:border-border/80 hover:text-foreground",
        disabled && "opacity-40 cursor-not-allowed",
        className,
      )}
    >
      {selected && (
        <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-blue-500 flex items-center justify-center">
          <Check className="h-2 w-2 stroke-[3] text-white" />
        </span>
      )}
      {icon}
      <span className="text-label font-medium leading-tight">{label}</span>
    </button>
  );

  if (layout === "tile") {
    if (!tooltip) return tileButton;
    return (
      <Tooltip>
        <TooltipTrigger asChild>{tileButton}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-[220px] text-left">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );
  }

  const rowButton = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all",
        selected
          ? "border-blue-500/40 bg-blue-500/[0.07] text-foreground"
          : "border-border/40 text-muted-foreground/70 hover:border-border/70 hover:text-foreground",
        disabled && "opacity-40 cursor-not-allowed",
        className,
      )}
    >
      <div
        className={cn(
          "h-3.5 w-3.5 rounded border shrink-0 flex items-center justify-center transition-all",
          selected ? "bg-blue-600 border-blue-600" : "border-border/60",
        )}
      >
        {selected && <Check className="h-2 w-2 stroke-[3] text-white" />}
      </div>
      {icon}
      <span className="text-md font-medium">{label}</span>
      {description && (
        <span className="text-sm text-subtle-foreground hidden sm:block ml-auto">{description}</span>
      )}
      {trailing && (
        <span className={cn("text-sm font-medium shrink-0 ml-auto", selected ? "text-blue-400" : "text-subtle-foreground")}>
          {trailing}
        </span>
      )}
    </button>
  );

  if (!tooltip) return rowButton;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{rowButton}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] text-left">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
